import { useIsGreek } from "../context/LanguageContext";
import { mapProgressMessage, mapSimulationError } from "./feasibility/simulationProgress";
// src/pages/Optimize.jsx
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  Paper,
  Typography,
  Stack,
  Chip,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Button,
  Alert,
  Box,
  Divider,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Snackbar,
  InputAdornment,
  Pagination,
  Checkbox,
} from "@mui/material";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useEngine } from "../context/EngineContext";
import { useDemo } from "../context/DemoContext";
import {
  runEngine,
  listRunsByProject,
  requestRunStreamTicket,
  requestRunsStreamTicket,
  saveScheduleSettings,
  getScheduleSettings,
  fetchDamPrices,
  updateScheduleInputForecast,
  downloadLatestPvDetails,
} from "../services/api";
import { useAuth } from "../context/AuthContext";
import { useFile } from "../context/FileContext";
import { useHistory } from "../context/HistoryContext";
import {
  getSupportedMarketZone,
  getSupportedMarketsLabel,
  isUnsupportedMarketZone,
  normalizeMarketZone,
  SUPPORTED_MARKETS,
} from "../utils/marketSupport";
import { formatApiError } from "../utils/apiErrors";
import { getDemoActionDisabledMessage } from "../utils/demoAccess";
import AppDatePicker from "../components/common/AppDatePicker";
import {
  deliveryDayBounds, deliveryDayFetchRange, deliveryDayInZone, formatRunDeliveryPeriod, MARKET_DELIVERY_TIME_ZONE,
  operationalTimeZone, runDeliveryPeriod, runIncludesDeliveryDay, validDeliveryDay,
} from "../utils/operationalDeliveryDay";
// ------------------ CONSTANTS --------------------
const STATUS_KEY = "meten_last_status";
const LAST_RUN_KEY = "meten_last_run"; // "YYYY-MM-DD_HH:MM"
const DELIVERY_DAY_KEY = "meten_delivery_day";
const MIN_RUN_TIME = "08:10";
const MAX_RUN_TIME = "23:59";
const LAST_SAVED_SCHEDULE_MAP_KEY = "meten_last_saved_schedule_map";
const LAST_SAVED_SCHEDULE_LAST_PROJECT = "meten_last_saved_schedule_last_project";
const AVAILABLE_MARKETS = SUPPORTED_MARKETS;
const QUARTER_HOUR_STEP_MS = 15 * 60 * 1000;
// Only DAM is wired into the dispatch engine today; IDA1-3 are captured for when
// backend support for multi-market dispatch lands (see marketParticipation UI below).
const DEFAULT_MARKET_PARTICIPATION = { dam: true, ida1: false, ida2: false, ida3: false };
const MARKET_PARTICIPATION_OPTIONS = [
  { key: "dam", label: "DAM", locked: true },
  { key: "ida1", label: "IDA1 Auction", locked: false },
  { key: "ida2", label: "IDA2 Auction", locked: false },
  { key: "ida3", label: "IDA3 Auction", locked: false },
];

// ------------------ HELPERS --------------------
function pad2(n) {
  return String(n).padStart(2, "0");
}

function parseDateInput(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr)
    .split("-")
    .map((x) => parseInt(x, 10));
  if (!y || !m || !d) return null;
  return { year: y, month: m, day: d };
}

function addDaysToDateInput(dateStr, days) {
  const parts = parseDateInput(dateStr);
  if (!parts) return "";
  const next = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
  return `${next.getUTCFullYear()}-${pad2(next.getUTCMonth() + 1)}-${pad2(next.getUTCDate())}`;
}

function isValidTimeZone(timeZone) {
  if (!timeZone) return false;
  try {
    Intl.DateTimeFormat("en-US", { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

function getSafeTimeZone(timeZone) {
  if (isValidTimeZone(timeZone)) return timeZone;
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

function getTimeZoneParts(date, timeZone) {
  const safeTimeZone = getSafeTimeZone(timeZone);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: safeTimeZone,
    hour12: false,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date);
  const map = Object.fromEntries(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value])
  );
  return {
    year: parseInt(map.year, 10),
    month: parseInt(map.month, 10),
    day: parseInt(map.day, 10),
    hour: parseInt(map.hour, 10) % 24,
    minute: parseInt(map.minute, 10),
    second: parseInt(map.second, 10),
  };
}

function getTimeZoneOffsetMs(date, timeZone) {
  const parts = getTimeZoneParts(date, timeZone);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    date.getMilliseconds()
  );
  return asUtc - date.getTime();
}

function buildProjectDateTime(dateStr, timeZone, endOfDay = false) {
  const parts = parseDateInput(dateStr);
  if (!parts) return null;
  return buildProjectDateTimeAtTime(
    parts.year,
    parts.month,
    parts.day,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0,
    timeZone
  );
}

function buildProjectDateTimeAtTime(year, month, day, hour, minute, second, millisecond, timeZone) {
  const targetUtc = Date.UTC(
    year,
    month - 1,
    day,
    hour,
    minute,
    second,
    millisecond
  );
  let guess = targetUtc;
  for (let i = 0; i < 5; i += 1) {
    const offset = getTimeZoneOffsetMs(new Date(guess), timeZone);
    const nextGuess = targetUtc - offset;
    if (Math.abs(nextGuess - guess) < 1) {
      guess = nextGuess;
      break;
    }
    guess = nextGuess;
  }
  return new Date(guess);
}

function parseProjectDateTimeValue(value, timeZone) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  // If the API already includes an offset or UTC marker, trust it.
  if (/([zZ]|[+-]\d{2}:\d{2}|[+-]\d{4})$/.test(raw)) {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2})(?::(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?)?)?$/
  );
  if (!match) {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const [, year, month, day, hour = "00", minute = "00", second = "00", fraction = "0"] = match;
  return buildProjectDateTimeAtTime(
    Number(year),
    Number(month),
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
    Number(fraction.padEnd(3, "0")),
    timeZone
  );
}

function getDateRangeBounds(startDateStr, endDateStr, timeZone) {
  const start = buildProjectDateTime(startDateStr, timeZone, false);
  const nextDayStr = addDaysToDateInput(endDateStr, 1);
  const endExclusive = nextDayStr
    ? buildProjectDateTime(nextDayStr, timeZone, false)
    : null;
  if (
    !(start instanceof Date) ||
    Number.isNaN(start?.getTime?.()) ||
    !(endExclusive instanceof Date) ||
    Number.isNaN(endExclusive?.getTime?.()) ||
    start.getTime() >= endExclusive.getTime()
  ) {
    return null;
  }
  return { start, endExclusive };
}

function formatDateTimeWithOffset(date, timeZone) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  const parts = timeZone ? getTimeZoneParts(date, timeZone) : null;
  const offsetMinutes = timeZone
    ? Math.round(getTimeZoneOffsetMs(date, timeZone) / 60000)
    : -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMinutes);
  const offsetH = pad2(Math.floor(abs / 60));
  const offsetM = pad2(abs % 60);

  const year = parts?.year ?? date.getFullYear();
  const month = parts?.month ?? date.getMonth() + 1;
  const day = parts?.day ?? date.getDate();
  const hour = parts?.hour ?? date.getHours();
  const minute = parts?.minute ?? date.getMinutes();
  const second = parts?.second ?? date.getSeconds();

  return `${year}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(minute)}:${pad2(second)}${sign}${offsetH}:${offsetM}`;
}

function triggerFileDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function getMinutesFromHHMM(hhmm) {
  const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
  return h * 60 + m;
}

function normalizeTimeValue(value) {
  const fallback = MIN_RUN_TIME;
  if (!value || typeof value !== "string") return fallback;
  const parts = value.split(":");
  if (parts.length < 2) return fallback;
  const h = Math.min(Math.max(parseInt(parts[0], 10) || 0, 0), 23);
  const m = Math.min(Math.max(parseInt(parts[1], 10) || 0, 0), 59);
  const minutes = h * 60 + m;
  const minMinutes = getMinutesFromHHMM(MIN_RUN_TIME);
  const maxMinutes = getMinutesFromHHMM(MAX_RUN_TIME);
  const clampedMinutes = Math.min(Math.max(minutes, minMinutes), maxMinutes);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(Math.floor(clampedMinutes / 60))}:${pad(clampedMinutes % 60)}`;
}

function timeStringToDate(value) {
  if (!value) return null;
  const [h, m] = normalizeTimeValue(value).split(":").map((x) => parseInt(x, 10));
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
}

function dateToTimeString(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return MIN_RUN_TIME;
  const pad = (n) => String(n).padStart(2, "0");
  return normalizeTimeValue(`${pad(date.getHours())}:${pad(date.getMinutes())}`);
}

function normalizeMarket(value) {
  const upper = String(value || "").trim().toUpperCase();
  return AVAILABLE_MARKETS.includes(upper) ? upper : AVAILABLE_MARKETS[0];
}

function toNumberSafe(value) {
  if (value == null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function toTwoDecimals(value) {
  const num = toNumberSafe(value);
  if (num == null) return null;
  return Number(num.toFixed(2));
}

function quarterHourKey(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const aligned = new Date(d.getTime());
  aligned.setSeconds(0, 0);
  return aligned.getTime();
}

function buildSeriesTimeline(series, startDateStr, endDateStr, timeZone, stepMs = QUARTER_HOUR_STEP_MS) {
  const bounds = getDateRangeBounds(startDateStr, endDateStr, timeZone);
  if (!bounds) return [];
  const startMs = bounds.start.getTime();
  const endExclusiveMs = bounds.endExclusive.getTime();
  const timeMap = new Map();
  series.forEach((row) => {
    const key = quarterHourKey(row?.time);
    if (key != null) timeMap.set(key, row);
  });
  const result = [];
  const step = Number.isFinite(stepMs) && stepMs > 0 ? stepMs : QUARTER_HOUR_STEP_MS;
  for (let key = startMs; key < endExclusiveMs; key += step) {
    const row = timeMap.get(key);
    result.push(row ? { ...row, time: new Date(key) } : { time: new Date(key) });
  }
  return result;
}

function buildInputForecastMap(points = []) {
  const map = new Map();
  (Array.isArray(points) ? points : []).forEach((row) => {
    const key = quarterHourKey(row?.ts || row?.time || row?.timestamp);
    const price = toNumberSafe(row?.price_eur_mwh ?? row?.price);
    if (key != null && price != null) {
      map.set(key, price);
    }
  });
  return map;
}

function getSeriesValueAt(series, index) {
  const value = Array.isArray(series) ? series[index] : undefined;
  return value == null || value === "" ? "" : String(value);
}

function getMtuEndTime(rows, index) {
  const current = rows[index];
  if (!(current instanceof Date) || Number.isNaN(current.getTime())) return null;
  const next = rows[index + 1];
  if (next instanceof Date && !Number.isNaN(next.getTime())) return next;
  const previous = rows[index - 1];
  if (previous instanceof Date && !Number.isNaN(previous.getTime())) {
    return new Date(current.getTime() + (current.getTime() - previous.getTime()));
  }
  return new Date(current.getTime() + QUARTER_HOUR_STEP_MS);
}

function formatMtuRange(rows, index, timeZone) {
  const start = rows[index];
  if (!(start instanceof Date) || Number.isNaN(start.getTime())) return "-";
  const end = getMtuEndTime(rows, index);
  const startParts = getTimeZoneParts(start, timeZone);
  const datePart = `${pad2(startParts.day)}/${pad2(startParts.month)}/${startParts.year}`;
  const startTime = `${pad2(startParts.hour)}:${pad2(startParts.minute)}`;
  if (!(end instanceof Date) || Number.isNaN(end.getTime())) return `${datePart}, ${startTime}`;
  const endParts = getTimeZoneParts(end, timeZone);
  const endTime = `${pad2(endParts.hour)}:${pad2(endParts.minute)}`;
  return `${datePart}, ${startTime} - ${endTime}`;
}

function loadCachedSchedule(projectId) {
  if (!projectId) return null;
  try {
    const map = JSON.parse(localStorage.getItem(LAST_SAVED_SCHEDULE_MAP_KEY) || "{}");
    return map && typeof map === "object" ? map[projectId] || null : null;
  } catch {
    return null;
  }
}

function saveCachedSchedule(projectId, payload) {
  if (!projectId || !payload) return;
  try {
    const map = JSON.parse(localStorage.getItem(LAST_SAVED_SCHEDULE_MAP_KEY) || "{}");
    const next = map && typeof map === "object" ? map : {};
    next[projectId] = payload;
    localStorage.setItem(LAST_SAVED_SCHEDULE_MAP_KEY, JSON.stringify(next));
    localStorage.setItem(LAST_SAVED_SCHEDULE_LAST_PROJECT, projectId);
  } catch {
    // ignore cache errors
  }
}


function mapHistoryStatus(status, error) {
  const raw = String(status || "").toLowerCase();
  if (raw.includes("fail") || raw.includes("error")) {
    return { label: "Failed", severity: "error" };
  }
  if (raw.includes("complete") || raw.includes("success") || raw.includes("finish") || raw.includes("done")) {
    return { label: "Success", severity: "success" };
  }
  if (error) return { label: "Failed", severity: "error" };
  return { label: status || "Unknown", severity: "warning" };
}

function tryParseJson(value) {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      return JSON.parse(trimmed);
    } catch {
      // fall through to undefined
    }
  }
  return undefined;
}

function formatExecutionStart(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}, ${pad2(
    date.getHours()
  )}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
}

function formatRunErrorMessage(error) {
  const rawMessage = String(error?.message ?? error ?? "").trim();
  const parsedError = tryParseJson(rawMessage) || (typeof error === "object" ? error : undefined);
  const detailSource = parsedError?.detail || parsedError;
  const cleanedMessage = formatApiError(error, "Simulation failed.", { quota: "market" }).trim();
  const baseSentence = /[.!?]$/.test(cleanedMessage) ? cleanedMessage : `${cleanedMessage}.`;

  const startedAt =
    detailSource?.started_at ||
    detailSource?.start ||
    detailSource?.startedAt ||
    detailSource?.execution_start ||
    detailSource?.start_time ||
    parsedError?.started_at ||
    parsedError?.start;
  const executionText = formatExecutionStart(startedAt);
  return executionText ? `${baseSentence} Execution started at ${executionText}.` : baseSentence;
}

// Extract an error message from a run record (covers multiple backend field names)
function extractRunError(run = {}) {
  const normalize = (val) => {
    if (val === undefined || val === null || val === "") return "";
    if (typeof val === "string") return val;
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  };

  const candidates = [
    run.error_message,
    run.error,
    run.error?.message,
    run.error?.detail,
    run.error?.error_message,
    run.error?.errorMessage,
    run.error?.error_text,
    run.error?.error,
    run.errorMessage,
    run.error_text,
    run.error_msg,
    run.errorMsg,
    run.fail_reason,
    run.failure_reason,
    run.failureMessage,
    run.failMessage,
    run.reason,
    run.detail,
    run.error_detail,
    run.message,
    run.result?.error,
    run.result?.error_message,
    run.result?.errorMessage,
    run.result?.message,
    run.result?.detail,
    run.data?.error,
    run.data?.error_message,
    run.data?.errorMessage,
    run.data?.message,
    run.data?.detail,
    run.metadata?.error,
    run.metadata?.error_message,
    run.metadata?.errorMessage,
    run.metadata?.message,
    run.metadata?.detail,
  ];

  const found = candidates.find((c) => c !== undefined && c !== null && c !== "");
  return normalize(found);
}

function getUserFacingErrorMessage(message) {
  const normalized = String(message || "").trim();
  if (!normalized) return "Error details not provided";

  if (/open-meteo/i.test(normalized)) {
    return "Weather data failed to load. Please try again.";
  }

  return normalized;
}

// ----------------- COMPONENT -------------------
export default function Optimize() {
  const isGreek = useIsGreek();
  const { config, setResult } = useEngine();
  const { accessToken } = useAuth();
  const { demoProfile, refreshDemoProfile } = useDemo();
  const marketDemoRestrictionMessage = useMemo(
    () => getDemoActionDisabledMessage(demoProfile, "market"),
    [demoProfile]
  );
  const marketDemoActionDisabled = Boolean(marketDemoRestrictionMessage);
  const refreshDemoCounters = useCallback(async () => {
    try {
      await refreshDemoProfile();
    } finally {
      window.setTimeout(() => {
        void refreshDemoProfile();
      }, 1200);
    }
  }, [refreshDemoProfile]);
  const { currentProject } = useFile();
  const configHistory = useHistory();
  const navigate = useNavigate();

  const marketParticipation = useMemo(
    () => ({
      ...DEFAULT_MARKET_PARTICIPATION,
      ...(config?.marketParticipation || {}),
    }),
    [config?.marketParticipation]
  );
  const handleMarketParticipationChange = useCallback(
    (key) => (e) => {
      const checked = e.target.checked;
      const base = configHistory.present || {};
      configHistory.commit({
        ...base,
        marketParticipation: {
          ...DEFAULT_MARKET_PARTICIPATION,
          ...(base.marketParticipation || {}),
          [key]: checked,
        },
      });
    },
    [configHistory]
  );

  // Coupled energy markets share the CET/CEST delivery day.
  const projectTimeZone = MARKET_DELIVERY_TIME_ZONE;
  const assetTimeZone = useMemo(
    () => operationalTimeZone({ ...currentProject?.config, ...config }),
    [config, currentProject?.config]
  );
  const todayInProjectTimeZone = deliveryDayInZone(new Date(), projectTimeZone);
  const tomorrowInProjectTimeZone = addDaysToDateInput(todayInProjectTimeZone, 1);

  // Auto-run settings
  const [auto, setAuto] = useState(() => localStorage.getItem("meten_auto") === "1");
  const [time, setTime] = useState(() => normalizeTimeValue(localStorage.getItem("meten_time") || "09:00"));

  const [deliveryDay, setDeliveryDay] = useState(() => {
    const saved = localStorage.getItem(DELIVERY_DAY_KEY);
    return validDeliveryDay(saved) ? saved : tomorrowInProjectTimeZone;
  });
  const deliveryDayError = deliveryDay && !validDeliveryDay(deliveryDay)
    ? "Select a valid delivery day."
    : deliveryDay > tomorrowInProjectTimeZone
      ? "Delivery day cannot exceed D+1 from today."
      : "";

  // Persistent status
  const [status, setStatus] = useState(() => {
    const saved = localStorage.getItem(STATUS_KEY);
    return saved ? JSON.parse(saved) : null;
  });

  // Persistent run history
  const [history, setHistory] = useState([]);

  const [loading, setLoading] = useState(false);
  const runPoller = useRef(null);
  const runsStream = useRef(null);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [scheduleStatus, setScheduleStatus] = useState(null);
  const [scheduleNotice, setScheduleNotice] = useState("");
  const [scheduleActive, setScheduleActive] = useState(false);
  const [scheduleInputForecast, setScheduleInputForecast] = useState([]);

  // Historic Runs UI state
  const [historyDate, setHistoryDate] = useState(""); // "YYYY-MM-DD" or ""
  const [historyDeliveryDay, setHistoryDeliveryDay] = useState("");
  const [historyPage, setHistoryPage] = useState(0);
  const [historyGoTo, setHistoryGoTo] = useState("");
  const [errorDialog, setErrorDialog] = useState({ open: false, message: "" });
  const [pvDetailsDownloading, setPvDetailsDownloading] = useState(false);

  const [forecastReady, setForecastReady] = useState(false);
  // How the configured provider's published forecast for D+1 was built. null
  // until the check runs, or when no provider is configured (in which case
  // this page behaves exactly as it did before p27).
  const [forecastMode, setForecastMode] = useState(null);
  const [manualOverrideSeries, setManualOverrideSeries] = useState("");
  const [manualOverrideValues, setManualOverrideValues] = useState([]);
  const [autoOverrideSeries, setAutoOverrideSeries] = useState("");
  const [autoOverrideValues, setAutoOverrideValues] = useState([]);
  const [forecastSeries, setForecastSeries] = useState({ p10: [], p50: [], p90: [], actual: [] });
  const [forecastSeriesDay, setForecastSeriesDay] = useState("");
  useEffect(() => {
    setManualOverrideValues([]);
    setManualOverrideSeries("");
    setForecastSeriesDay("");
  }, [deliveryDay, currentProject?.id, projectTimeZone]);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastError, setForecastError] = useState("");
  const [forecastStatusLoading, setForecastStatusLoading] = useState(false);
  const [forecastStatusError, setForecastStatusError] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState("manual");
  const [previewSaving, setPreviewSaving] = useState(false);
  const [manualSelectedMtus, setManualSelectedMtus] = useState([]);
  const [autoSelectedMtus, setAutoSelectedMtus] = useState([]);

  const activeOverrideValues = previewMode === "automatic" ? autoOverrideValues : manualOverrideValues;
  const activeOverrideSeries = previewMode === "automatic" ? autoOverrideSeries : manualOverrideSeries;
  const setActiveOverrideValues =
    previewMode === "automatic" ? setAutoOverrideValues : setManualOverrideValues;
  const setActiveOverrideSeries =
    previewMode === "automatic" ? setAutoOverrideSeries : setManualOverrideSeries;
  const activeSelectedMtus = previewMode === "automatic" ? autoSelectedMtus : manualSelectedMtus;
  const setActiveSelectedMtus =
    previewMode === "automatic" ? setAutoSelectedMtus : setManualSelectedMtus;


  // Fetch schedule for the selected project (with local cache fallback per project)
  useEffect(() => {
    let active = true;
    setScheduleStatus(null);
    setScheduleNotice("");

    const applySchedule = (schedule = {}) => {
      if (!active) return;
      const runTime = schedule.run_time || "";
      const isActive = Boolean(schedule.active);
      setAuto(isActive);
      setTime(runTime ? normalizeTimeValue(runTime) : "");
      setScheduleActive(isActive);
      const parsedForecast =
        Array.isArray(schedule.input_forecast)
          ? schedule.input_forecast
          : tryParseJson(schedule.input_forecast);
      setScheduleInputForecast(Array.isArray(parsedForecast) ? parsedForecast : []);
      setScheduleNotice("");
    };

    const clearFields = () => {
      if (!active) return;
      setAuto(false);
      setTime("");
      setScheduleActive(false);
      setScheduleInputForecast([]);
    };

    const markNoSchedule = () => {
      if (!active) return;
      clearFields();
      setScheduleNotice("No schedule set for this project.");
    };

    const projectId = currentProject?.id;
    if (!projectId) {
      clearFields();
      setScheduleNotice("");
      return;
    }

    const fetchSchedule = async () => {
      setLoadingSchedule(true);
      try {
        const data = await getScheduleSettings(projectId, accessToken);
        if (!active) return;

        if (data && typeof data === "object") {
          const runTime = data.run_time || "";
          const isActive = Boolean(data.active);
          applySchedule({ run_time: runTime, active: isActive });
          saveCachedSchedule(projectId, {
            run_time: runTime,
            active: isActive,
          });
          try {
            localStorage.setItem(LAST_SAVED_SCHEDULE_LAST_PROJECT, projectId);
          } catch {}
        } else {
          const cached = loadCachedSchedule(projectId);
          if (cached) {
            applySchedule(cached);
          } else {
            markNoSchedule();
          }
        }
      } catch (err) {
        const message = err?.message || "";
        const isNotFound =
          typeof message === "string" &&
          (message.includes("Schedule not found") || message.includes("HTTP 404"));
        if (!isNotFound) {
          console.error("Failed to load schedule:", err);
        }
        if (active) {
          const cached = loadCachedSchedule(projectId);
          if (cached) {
            applySchedule(cached);
          } else {
            markNoSchedule();
          }
        }
      } finally {
        if (active) setLoadingSchedule(false);
      }
    };

    fetchSchedule();
    return () => {
      active = false;
    };
  }, [currentProject?.id, accessToken, assetTimeZone]);

  // Subscribe to runs list stream (automatic runs + status updates)
  useEffect(() => {
    let active = true;
    const projectId = currentProject?.id;
    if (!projectId) return undefined;

    const closeStream = () => {
      if (runsStream.current && typeof runsStream.current.close === "function") {
        runsStream.current.close();
      }
      runsStream.current = null;
    };

    const startStream = async () => {
      closeStream();
      const baseUrl = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
      let streamTicket;
      try {
        streamTicket = await requestRunsStreamTicket(projectId, accessToken);
      } catch (err) {
        if (active) {
          console.error(
            "Runs stream ticket request failed:",
            err?.status || err?.message || "unknown error"
          );
        }
        closeStream();
        return;
      }
      if (!active) return;

      const streamUrl = `${baseUrl}/runs/stream?project_id=${encodeURIComponent(
        projectId
      )}&stream_ticket=${encodeURIComponent(streamTicket)}`;
      const es = new EventSource(streamUrl, { withCredentials: true });
      runsStream.current = es;

      const upsertRun = (payload) => {
        const runId = payload?.run_id || payload?.id || payload?.runId;
        if (!runId) return;
        if (payload?.project_id && String(payload.project_id) !== String(projectId)) return;

        const start =
          payload?.started_at ||
          payload?.startedAt ||
          payload?.start ||
          payload?.created_at ||
          payload?.createdAt ||
          "";
        const end =
          payload?.finished_at ||
          payload?.ended_at ||
          payload?.completed_at ||
          payload?.end ||
          payload?.updated_at ||
          start ||
          "";
        const statusLabel = mapHistoryStatus(payload?.status, payload?.error_message).label;
        const errorMsg = extractRunError(payload);
        const period = runDeliveryPeriod(payload, assetTimeZone);

        setHistory((prev) => {
          const next = [...prev];
          const idx = next.findIndex((r) => String(r.id) === String(runId));
          const row = {
            id: runId,
            start,
            end,
            status: statusLabel,
            error: errorMsg,
            simStart: period.start,
            simEnd: period.end,
            deliveryDay: period.legacyRange ? "" : period.start,
            timezone: period.timeZone,
            automatic: payload?.automatic ?? "",
          };
          if (idx >= 0) {
            next[idx] = { ...next[idx], ...row };
          } else {
            next.unshift(row);
          }
          next.sort((a, b) => new Date(b.start || 0) - new Date(a.start || 0));
          return next;
        });
      };

      es.addEventListener("run", (event) => {
        try {
          const payload = JSON.parse(event.data || "{}");
          upsertRun(payload);
        } catch (err) {
          console.error("Runs stream parse error:", err);
        }
      });

      es.addEventListener("status", (event) => {
        try {
          const payload = JSON.parse(event.data || "{}");
          upsertRun(payload);
        } catch (err) {
          console.error("Runs stream parse error:", err);
        }
      });

      es.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data || "{}");
          upsertRun(payload);
        } catch (err) {
          console.error("Runs stream parse error:", err);
        }
      };

      es.addEventListener("error", () => {
        if (!active) return;
        console.error("Runs stream error");
      });
    };

    startStream();
    return () => {
      active = false;
      closeStream();
    };
  }, [currentProject?.id, accessToken, assetTimeZone]);

  // Auto-hide schedule status after 5s
  useEffect(() => {
    if (!scheduleStatus) return;
    const timer = setTimeout(() => setScheduleStatus(null), 5000);
    return () => clearTimeout(timer);
  }, [scheduleStatus]);

  // Save auto + time and reset lock when schedule changes
  useEffect(() => {
    localStorage.setItem("meten_auto", auto ? "1" : "0");
    localStorage.setItem("meten_time", time);
    localStorage.removeItem(LAST_RUN_KEY);
  }, [auto, time]);

  useEffect(() => {
    localStorage.setItem(DELIVERY_DAY_KEY, deliveryDay);
  }, [deliveryDay]);

  // Persist only terminal statuses so info/warnings don't stick after refresh
  useEffect(() => {
    const isTerminal = status?.sev === "success" || status?.sev === "error";
    if (status && isTerminal) {
      localStorage.setItem(STATUS_KEY, JSON.stringify(status));
    } else {
      localStorage.removeItem(STATUS_KEY);
    }
  }, [status]);

  // Auto-hide transient statuses (keep running/started simulation visible)
  useEffect(() => {
    if (!status) return;
    const isSimulation =
      typeof status.msg === "string" && status.msg.startsWith("Simulation status:");
    const isRunningLike = isSimulation && (status.sev === "info" || status.sev === "warning");
    if (isRunningLike) return;
  const autoHideMs =
      status.sev === "warning" ? 12000 : status.sev === "info" ? 8000 : 10000;
    const timer = setTimeout(() => setStatus(null), autoHideMs);
    return () => clearTimeout(timer);
  }, [status]);

  const fetchRemoteHistory = useCallback(async () => {
    if (!currentProject?.id) {
      setHistory([]);
      return;
    }
    try {
      const runs = await listRunsByProject(currentProject.id, accessToken);
      const mapped = (Array.isArray(runs) ? runs : []).map((r) => {
        const start =
          r.started_at ||
          r.startedAt ||
          r.started ||
          r.created_at ||
          r.createdAt ||
          r.start ||
          "";
        const end =
          r.ended_at ||
          r.completed_at ||
          r.finished_at ||
          r.end ||
          r.updated_at ||
          start ||
          "";
        const rawStatus = String(r.status || "").toLowerCase();
        const isFail = rawStatus.includes("fail") || rawStatus.includes("error");
        const isSuccess =
          rawStatus.includes("finish") ||
          rawStatus.includes("success") ||
          rawStatus.includes("done");
        const statusLabel = isFail ? "Failed" : isSuccess ? "Success" : r.status || "Unknown";
        const errorMsg = extractRunError(r);
        const runId = r.id || r.run_id || r.runId || r.uid || r._id || "";
        const period = runDeliveryPeriod(r, assetTimeZone);
        return {
          id: runId,
          start,
          end,
          status: statusLabel,
          error: errorMsg,
          simStart: period.start,
          simEnd: period.end,
          deliveryDay: period.legacyRange ? "" : period.start,
          timezone: period.timeZone,
          automatic: r.automatic ?? "",
        };
      });
      // Newest first by start time
      mapped.sort((a, b) => new Date(b.start || 0) - new Date(a.start || 0));
      setHistory(mapped);
    } catch (err) {
      console.error("Failed to fetch run history:", err);
      setHistory([]);
    }
  }, [currentProject?.id, accessToken, assetTimeZone]);

  useEffect(() => {
    fetchRemoteHistory();
  }, [fetchRemoteHistory]);

  const projectBiddingZone = useMemo(
    () =>
      normalizeMarketZone(
        config?.biddingZone ||
          config?.market ||
          config?.bidding_zone ||
          currentProject?.config?.biddingZone ||
          currentProject?.config?.market ||
          currentProject?.config?.bidding_zone
      ),
    [
      config?.biddingZone,
      config?.market,
      config?.bidding_zone,
      currentProject?.config?.biddingZone,
      currentProject?.config?.market,
      currentProject?.config?.bidding_zone,
    ]
  );
  const isHybridSystem = useMemo(() => {
    const systemTypeLabel = String(
      config?.systemType || currentProject?.config?.systemType || "Hybrid"
    );
    return systemTypeLabel.trim().toLowerCase() === "hybrid";
  }, [config?.systemType, currentProject?.config?.systemType]);
  const marketStrategyDisabled = useMemo(
    () => isUnsupportedMarketZone(projectBiddingZone),
    [projectBiddingZone]
  );
  const supportedMarketsLabel = useMemo(() => getSupportedMarketsLabel(), []);
  const forecastMarket = useMemo(
    () =>
      getSupportedMarketZone(projectBiddingZone) ||
      normalizeMarket(config?.biddingZone || config?.market || config?.bidding_zone),
    [projectBiddingZone, config?.biddingZone, config?.market, config?.bidding_zone]
  );

  // MUST be declared before the D+1 forecast-status effect below, which names
  // it in its dependency array - and a dependency array is evaluated DURING
  // render. p27 put this declaration ~260 lines further down, next to
  // forecastReadyForPreview, where it read naturally; the effect then hit the
  // const in its temporal dead zone and threw
  //   ReferenceError: Cannot access 'damForecastProvider' before initialization
  // during render, which unmounted the tree and rendered /optimize blank.
  // Babel parses that happily - it is valid syntax, not valid execution - so a
  // parse check could never have caught it.
  //
  // Resolved the same way the backend does it (see
  // dam_forecast_optimization_source.resolve_dam_forecast_provider):
  // damForecastProvider first, marketForecastSource as the older name. Falsy
  // means send no provider parameter, which is byte-for-byte the pre-p27
  // request - a project that has configured nothing sees no change.
  const damForecastProvider =
    currentProject?.config?.damForecastProvider ||
    currentProject?.config?.marketForecastSource ||
    "";
  const manualMtuCount = useMemo(
    () => deliveryDayBounds(deliveryDay, projectTimeZone)?.intervalCount || 0,
    [deliveryDay, projectTimeZone]
  );
  const autoMtuCount = useMemo(
    () => buildSeriesTimeline([], tomorrowInProjectTimeZone, tomorrowInProjectTimeZone, projectTimeZone).length,
    [projectTimeZone, tomorrowInProjectTimeZone]
  );

  const forecastUnit = "\u20ac/MWh";

  // ------------------ RUN NOW ---------------------
  const goToRunResults = useCallback(
    (runId) => {
      if (!runId) return;
      navigate(`/dashboard?view=optimization${runId ? `&runId=${encodeURIComponent(runId)}` : ""}`);
      window.scrollTo({ top: 0, behavior: "auto" });
    },
    [navigate]
  );

  const runNow = useCallback(
    async () => {
      if (loading) return;
      if (marketDemoRestrictionMessage) {
        setStatus({
          sev: "warning",
          msg: marketDemoRestrictionMessage,
        });
        return;
      }
      if (marketStrategyDisabled) {
        setStatus({
          sev: "warning",
          msg: `Bidding zone ${projectBiddingZone} is not supported for Market Strategy. Supported zones: ${supportedMarketsLabel}.`,
        });
        return;
      }

      try {
        if (!config || Object.keys(config).length === 0) {
          setStatus({
            sev: "warning",
            msg: "No config found. Set it in PV Setup first.",
          });
          return;
        }

        const projectIdForRun = currentProject?.id || null;
        if (!projectIdForRun) {
          setStatus({
            sev: "warning",
            msg: "A project must be loaded prior to running. Please load a project.",
          });
          setTimeout(() => setStatus(null), 10000);
          return;
        }

        const bounds = deliveryDayBounds(deliveryDay, projectTimeZone);
        if (!bounds || deliveryDayError) {
          setStatus({ sev: "warning", msg: deliveryDayError || "Select a delivery day first." });
          return;
        }

        const expectedCount = manualMtuCount;
        if (expectedCount > 0) {
          const normalizeSeries = (values) =>
            (Array.isArray(values) ? values : [])
              .slice(0, expectedCount)
              .map((v) => toTwoDecimals(v));
          const isComplete = (values) =>
            values.length === expectedCount && values.every((v) => v != null);

          let series = normalizeSeries(manualOverrideValues);

          if (!isComplete(series)) {
            const p50Series = normalizeSeries(forecastSeriesDay === deliveryDay ? forecastSeries.p50 || [] : []);
            if (isComplete(p50Series)) {
              series = p50Series;
            } else {
              const startStr = deliveryDay || "";
              const endStr = deliveryDay || "";
              if (startStr && endStr) {
                try {
                  const res = await fetchDamPrices(
                    {
                      market: forecastMarket, ...deliveryDayFetchRange(startStr, assetTimeZone), series: "both",
                      // The SAME provider as the badge and the price table. This
                      // call decides whether a manual run is allowed to start at
                      // all ("Forecast prices (P50) are not available for the
                      // selected delivery day." below), and it was the only one of the
                      // three still reading the legacy Forecast Valley column -
                      // so a manual run could be blocked on a day our own
                      // forecast had published all 96 intervals.
                      provider: damForecastProvider || undefined,
                    },
                    accessToken
                  );
                  const rawRows = Array.isArray(res?.data)
                    ? res.data
                    : Array.isArray(res)
                      ? res
                      : [];
                  const data = rawRows.map((row) => ({
                    time: parseProjectDateTimeValue(
                      row.delivery_time ?? row.time ?? null,
                      assetTimeZone
                    ),
                    p50: toNumberSafe(row.p50),
                  }));
                  const finalSeries = buildSeriesTimeline(data, startStr, endStr, projectTimeZone);
                  const fetchedP50 = finalSeries.map((row) => row.p50 ?? null);
                  const normalizedFetched = normalizeSeries(fetchedP50);
                  if (isComplete(normalizedFetched)) {
                    series = normalizedFetched;
                    setForecastSeriesDay(deliveryDay);
                    setForecastSeries((prev) => ({
                      p10: prev?.p10 || [],
                      p90: prev?.p90 || [],
                      p50: finalSeries.map((row) => row.p50 ?? ""),
                      actual: prev?.actual || [],
                    }));
                  }
                } catch (err) {
                  console.error("Failed to load forecast series for manual run:", err);
                }
              }
            }
          }

          if (!isComplete(series)) {
            setStatus({
              sev: "warning",
              msg: "Forecast prices (P50) are not available for the selected delivery day.",
            });
            return;
          }

          const nextSeries = series.map((v) => (v == null ? "" : String(v)));
          setManualOverrideValues(nextSeries);
          setManualOverrideSeries(nextSeries.join(" "));

          // Attach manual override series (Option A: list aligned to start/end/resolution).
          var manualOverrideSeries = series;
        }

        setLoading(true);

        const nowIso = new Date().toISOString();
        const optimisticId = `pending-${Date.now()}`;
        const optimisticRow = {
          id: optimisticId,
          start: nowIso,
          end: "",
          status: "Running",
          error: "",
          deliveryDay,
          timezone: projectTimeZone,
          simStart: deliveryDay,
          simEnd: deliveryDay,
          automatic: "No",
          pending: true,
        };
        setHistory((prev) => [optimisticRow, ...prev]);

        setStatus({ sev: "info", msg: "Simulation status: Started" });

        const payload = {
          project_id: projectIdForRun,
          delivery_day: deliveryDay,
          price_override_series: manualOverrideSeries,
        };
        const data = await runEngine(payload, accessToken);
        const runId = data?.run_id || data?.id || data?.runId;

        if (runId) {
          setHistory((prev) =>
            prev.map((r) =>
              r.id === optimisticId
                ? { ...r, id: runId, pending: false }
                : r
            )
          );
        }

        if (!runId) {
          setStatus({ sev: "info", msg: "Simulation status: Started" });
          // Without run id, stop loading to avoid spinner lock
          setLoading(false);
          return;
        }

        const normalizeStatus = (lower, hasResult = false) => {
          const l = lower || "";
          if (["completed", "finished", "success", "done", "complete", "succeeded"].includes(l)) {
            return "finished";
          }
          if (["running", "processing", "in_progress", "in-progress"].includes(l)) {
            return "running";
          }
          if (["failed", "error"].includes(l)) {
            return "failed";
          }
          if (["queued", "pending", "enqueued", "started"].includes(l)) {
            return hasResult ? "finished" : "started";
          }
          if (hasResult) return "finished";
          // Default to running instead of unknown to avoid getting stuck
          return "running";
        };

        const updateFromStatus = (normalized, raw) => {
          const label =
            normalized === "finished"
              ? "Finished"
              : normalized === "running"
                ? "Running"
                : normalized === "failed"
                  ? "Failed"
                  : normalized === "started"
                    ? "Started"
                    : "Unknown";

          const sev =
            normalized === "failed"
              ? "error"
              : normalized === "finished"
                ? "success"
                : "info";

          setStatus({ sev, msg: `Simulation status: ${label}` });
        };

        const initialStatusVal =
          data?.status ?? data?.run_status ?? "";
        const initialNormalized = normalizeStatus(
          initialStatusVal ? String(initialStatusVal).toLowerCase().trim() : "",
          Boolean(data?.result || data?.results)
        );
        updateFromStatus(initialNormalized, data?.status || data?.run_status);
        if (initialNormalized === "finished") {
          setResult(data?.result || data?.results || null);
          fetchRemoteHistory();
          void refreshDemoCounters();
          setLoading(false);
          return;
        }

        // Clear any previous stream
        if (runPoller.current) {
          if (typeof runPoller.current.close === "function") {
            runPoller.current.close();
          } else {
            clearInterval(runPoller.current);
          }
        }

        const baseUrl = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
        let streamTicket;
        try {
          streamTicket = await requestRunStreamTicket(runId, accessToken);
        } catch (err) {
          console.error(
            "Run status stream ticket request failed:",
            err?.status || err?.message || "unknown error"
          );
          if (runPoller.current && typeof runPoller.current.close === "function") {
            runPoller.current.close();
          }
          runPoller.current = null;
          fetchRemoteHistory();
          setLoading(false);
          return;
        }
        const streamUrl = `${baseUrl}/runs/${encodeURIComponent(
          runId
        )}/stream?stream_ticket=${encodeURIComponent(streamTicket)}`;
        const es = new EventSource(streamUrl, { withCredentials: true });
        runPoller.current = es;

        const handleStatus = (payload) => {
          const statusVal = payload?.status ?? "";
          const normalized = normalizeStatus(
            statusVal ? String(statusVal).toLowerCase().trim() : "",
            Boolean(payload?.result || payload?.results)
          );
          updateFromStatus(normalized, statusVal);

          if (["finished", "failed"].includes(normalized)) {
            fetchRemoteHistory();
            if (normalized === "finished") {
              void refreshDemoCounters();
            }
            if (normalized === "failed") setResult(null);
            if (runPoller.current && typeof runPoller.current.close === "function") {
              runPoller.current.close();
            }
            runPoller.current = null;
            setLoading(false);
          }
        };

        es.addEventListener("status", (event) => {
          try {
            const payload = JSON.parse(event.data || "{}");
            handleStatus(payload);
          } catch (err) {
            console.error("Run status parse error:", err);
          }
        });

        es.addEventListener("error", () => {
          console.error("Run status stream error");
          if (runPoller.current && typeof runPoller.current.close === "function") {
            runPoller.current.close();
          }
          runPoller.current = null;
          fetchRemoteHistory();
          setLoading(false);
        });
      } catch (e) {
        console.error("Optimization error:", e);
        if (e?.status === 403) {
          void refreshDemoCounters();
        }
        const formattedRunError = formatRunErrorMessage(e);
        const lockedAccountFallback =
          e?.status === 403 &&
          /failed to fetch|networkerror|network error|load failed/i.test(formattedRunError) &&
          demoProfile?.demoLocked;

        setStatus({
          sev: "error",
          msg: lockedAccountFallback ? "Demo account is locked." : formattedRunError,
        });

        fetchRemoteHistory();
        setLoading(false);
      } finally {
        // Loading is stopped when polling completes or if run id is missing
        if (!runPoller.current) {
          setLoading(false);
        }
      }
    },
    [
      config,
      loading,
      setResult,
      accessToken,
      deliveryDay,
      deliveryDayError,
      forecastSeriesDay,
      currentProject?.id,
      fetchRemoteHistory,
      manualMtuCount,
      manualOverrideValues,
      forecastMarket,
      forecastSeries.p50,
      marketStrategyDisabled,
      projectTimeZone,
      assetTimeZone,
      projectBiddingZone,
      supportedMarketsLabel,

      demoProfile?.demoLocked,
      marketDemoRestrictionMessage,
      refreshDemoCounters,
      damForecastProvider,
    ]
  );

  const handleSaveSchedule = useCallback(async () => {
    setScheduleStatus(null);

    if (marketDemoRestrictionMessage && auto) {
      setScheduleStatus({
        sev: "warning",
        msg: marketDemoRestrictionMessage,
      });
      return;
    }

    if (marketStrategyDisabled) {
      setScheduleStatus({
        sev: "warning",
        msg: `Bidding zone ${projectBiddingZone} is not supported for Market Strategy. Supported zones: ${supportedMarketsLabel}.`,
      });
      return;
    }

    const projectIdForRun = currentProject?.id || null;
    if (!projectIdForRun) {
      setScheduleStatus({ sev: "warning", msg: "Select a project before saving the schedule." });
      return;
    }

    if (!time) {
      setScheduleStatus({ sev: "warning", msg: "Select a run time before saving." });
      return;
    }

    const normalizedRunTime = normalizeTimeValue(time);
    setSavingSchedule(true);
    try {
      await saveScheduleSettings(
        projectIdForRun,
        {
          run_time: normalizedRunTime || null,
          active: Boolean(auto),
        },
        accessToken
      );
      saveCachedSchedule(projectIdForRun, {
        run_time: normalizedRunTime,
        active: Boolean(auto),
      });
      setScheduleActive(Boolean(auto));
      setScheduleNotice("");
      try {
        localStorage.setItem(LAST_SAVED_SCHEDULE_LAST_PROJECT, projectIdForRun);
      } catch {}
      setScheduleStatus({ sev: "success", msg: auto ? "Shcedule is saved." : "Scedule is disabled." });
    } catch (err) {
      setScheduleStatus({
        sev: "error",
        msg: formatApiError(err, "Failed to save schedule.", { quota: "market" }),
      });
    } finally {
      setSavingSchedule(false);
    }
  }, [auto, time, accessToken, currentProject?.id, marketDemoRestrictionMessage, marketStrategyDisabled, projectBiddingZone, supportedMarketsLabel]);

  // Cleanup poller on unmount
  useEffect(() => {
    return () => {
      if (runPoller.current) {
        if (typeof runPoller.current.close === "function") {
          runPoller.current.close();
        } else {
          clearInterval(runPoller.current);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (marketStrategyDisabled) {
      setForecastReady(false);
      setForecastStatusError("");
      setForecastStatusLoading(false);
      return;
    }
    if (!accessToken) return;
    let active = true;
    let intervalId = null;
    let timeoutId = null;

    const withinCheckWindow = () => {
      const now = getTimeZoneParts(new Date(), assetTimeZone);
      return now.hour >= 8;
    };

    const nextCheckDelayMs = () => {
      const now = new Date();
      const nowParts = getTimeZoneParts(now, assetTimeZone);
      const todayStr = `${nowParts.year}-${pad2(nowParts.month)}-${pad2(nowParts.day)}`;
      const targetDateStr = nowParts.hour >= 8 ? addDaysToDateInput(todayStr, 1) : todayStr;
      const targetParts = parseDateInput(targetDateStr);
      const next = targetParts
        ? buildProjectDateTimeAtTime(
          targetParts.year,
          targetParts.month,
          targetParts.day,
          8,
          0,
          0,
          0,
          assetTimeZone
        )
        : null;
      if (!(next instanceof Date) || Number.isNaN(next.getTime())) return 5 * 60 * 1000;
      return Math.max(0, next.getTime() - now.getTime());
    };

    const runCheck = () => {
      const dateStr = tomorrowInProjectTimeZone;
      if (!dateStr) return;
      setForecastStatusLoading(true);
      setForecastStatusError("");
      fetchDamPrices(
        {
          market: forecastMarket, ...deliveryDayFetchRange(dateStr, assetTimeZone), series: "both",
          // THE FIX: ask the provider this project actually prices against.
          // Without this the badge went green off Forecast Valley's column
          // while the configured provider had published nothing.
          provider: damForecastProvider || undefined,
        },
        accessToken
      )
        .then((res) => {
          if (!active) return;
          const rawRows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
          const data = rawRows.map((row) => ({
            time: parseProjectDateTimeValue(row.delivery_time ?? row.time ?? null, assetTimeZone),
            p50: toNumberSafe(row.p50),
          }));
          const finalSeries = buildSeriesTimeline(data, dateStr, dateStr, projectTimeZone);
          const hasValues = finalSeries.length > 0 && finalSeries.every((row) => Number.isFinite(row.p50));
          setForecastReady(hasValues);
          setForecastMode(res?.forecastMode || null);
        })
        .catch((err) => {
          if (!active) return;
          setForecastReady(false);
          setForecastMode(null);
          setForecastStatusError(err?.message || String(err));
        })
        .finally(() => {
          if (active) setForecastStatusLoading(false);
        });
    };

    const schedule = () => {
      if (!active) return;
      if (withinCheckWindow()) {
        runCheck();
        intervalId = setInterval(() => {
          if (withinCheckWindow()) {
            runCheck();
          }
        }, 5 * 60 * 1000);
        // keep checking until cleanup; after 08:00, polling continues periodically
      } else {
        const delay = nextCheckDelayMs();
        timeoutId = setTimeout(schedule, delay);
      }
    };

    schedule();
    return () => {
      active = false;
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [forecastMarket, accessToken, projectTimeZone, assetTimeZone, tomorrowInProjectTimeZone, marketStrategyDisabled, damForecastProvider]);

  useEffect(() => {
    if (marketStrategyDisabled) {
      setForecastSeries({ p10: [], p50: [], p90: [], actual: [] });
      setActiveOverrideValues([]);
      setActiveOverrideSeries("");
      setForecastError("");
      return;
    }
    if (!previewOpen) return;
    let startStr = "";
    let endStr = "";
    if (previewMode === "manual") {
      startStr = deliveryDay || "";
      endStr = deliveryDay || "";
    } else {
      startStr = tomorrowInProjectTimeZone;
      endStr = tomorrowInProjectTimeZone;
    }
    if (!startStr || !endStr) {
      setForecastSeries({ p10: [], p50: [], p90: [], actual: [] });
      setActiveOverrideValues([]);
      setActiveOverrideSeries("");
      setForecastError("Select a delivery day to load forecast series.");
      return;
    }
    let active = true;
    setForecastLoading(true);
    setForecastSeriesDay("");
    setForecastError("");
    const isTomorrowOnly =
      previewMode !== "manual" &&
      startStr === tomorrowInProjectTimeZone &&
      endStr === startStr;
    fetchDamPrices(
      {
        market: forecastMarket, ...deliveryDayFetchRange(startStr, assetTimeZone), series: "both",
        // Same provider as the badge above. A badge sourced from one provider
        // over a price table drawn from another would be worse than the bug
        // this replaces. Consequence, stated plainly: on a day the configured
        // provider published nothing this table is EMPTY rather than showing a
        // number the optimization would never have used.
        provider: damForecastProvider || undefined,
      },
      accessToken
    )
      .then((res) => {
        if (!active) return;
        const rawRows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        const data = rawRows.map((row) => ({
          time: parseProjectDateTimeValue(row.delivery_time ?? row.time ?? null, assetTimeZone),
          p10: toNumberSafe(row.p10),
          p50: toNumberSafe(row.p50),
          p90: toNumberSafe(row.p90),
          actual: toNumberSafe(row.actual),
        }));
        const finalSeries = buildSeriesTimeline(data, startStr, endStr, projectTimeZone);
        const hasAnyForecast = finalSeries.some(
          (row) => row.p10 != null || row.p50 != null || row.p90 != null || row.actual != null
        );
        if (isTomorrowOnly) {
          const hasValues = finalSeries.length > 0 && finalSeries.every((row) => Number.isFinite(row.p50));
          setForecastReady(hasValues);
        }
        setForecastSeriesDay(startStr);
        setForecastSeries({
          p10: finalSeries.map((row) => row.p10 ?? ""),
          p50: finalSeries.map((row) => row.p50 ?? ""),
          p90: finalSeries.map((row) => row.p90 ?? ""),
          actual: finalSeries.map((row) => row.actual ?? ""),
        });
        if (!hasAnyForecast) {
          setActiveOverrideValues([]);
          setActiveOverrideSeries("");
        }
      })
      .catch((err) => {
        if (!active) return;
        setForecastSeries({ p10: [], p50: [], p90: [], actual: [] });
        setActiveOverrideValues([]);
        setActiveOverrideSeries("");
        if (isTomorrowOnly) {
          setForecastReady(false);
        }
        setForecastError(err?.message || String(err));
      })
      .finally(() => {
        if (active) setForecastLoading(false);
      });
    return () => {
      active = false;
    };
  }, [
    previewOpen,
    previewMode,
    deliveryDay,
    forecastMarket,
    accessToken,
    projectTimeZone,
    assetTimeZone,
    tomorrowInProjectTimeZone,
    setActiveOverrideValues,
    setActiveOverrideSeries,
    marketStrategyDisabled,
    damForecastProvider,
  ]);

  const previewRange = useMemo(() => {
    if (previewMode === "manual") {
      return {
        start: deliveryDay || "",
        end: deliveryDay || "",
      };
    }
    return {
      start: tomorrowInProjectTimeZone,
      end: tomorrowInProjectTimeZone,
    };
  }, [previewMode, deliveryDay, tomorrowInProjectTimeZone]);

  const previewTimestamps = useMemo(
    () => buildSeriesTimeline([], previewRange.start, previewRange.end, projectTimeZone).map((row) => row.time),
    [previewRange.end, previewRange.start, projectTimeZone]
  );
  const previewMtuCount = previewTimestamps.length;
  const previewRangeLabel = useMemo(() => {
    if (!previewRange.start) return "Select a delivery day to populate manual timestamps.";
    const startDate = buildProjectDateTime(previewRange.start, projectTimeZone, false);
    if (!(startDate instanceof Date) || Number.isNaN(startDate.getTime())) {
      return "Select a delivery day to populate manual timestamps.";
    }
    const formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: projectTimeZone,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const startLabel = formatter.format(startDate);
    return `Delivery day: ${startLabel} (${projectTimeZone})`;
  }, [previewRange.start, projectTimeZone]);

  useEffect(() => {
    if (!previewOpen) return;
    setActiveOverrideValues((prev) => {
      const parsed = activeOverrideSeries.split(/[\s,]+/).filter(Boolean);
      const values = Array.from({ length: previewMtuCount }, (_, i) => {
        const v = prev[i];
        if (v !== undefined) return v;
        return parsed[i] ?? "";
      });
      const same = values.length === prev.length && values.every((v, i) => v === prev[i]);
      return same ? prev : values;
    });
  }, [
    previewOpen,
    previewMtuCount,
    activeOverrideSeries,
    previewMode,
    setActiveOverrideValues,
    setActiveOverrideSeries,
  ]);

  useEffect(() => {
    if (!previewOpen) return;
    setActiveSelectedMtus((prev) =>
      Array.from({ length: previewMtuCount }, (_, i) =>
        typeof prev[i] === "boolean" ? prev[i] : true
      )
    );
  }, [previewOpen, previewMtuCount, previewMode, setActiveSelectedMtus]);

  useEffect(() => {
    if (!previewOpen || previewMode !== "automatic") return;
    if (!scheduleInputForecast.length || previewMtuCount === 0) return;
    const map = buildInputForecastMap(scheduleInputForecast);
    if (!map.size) return;
    const next = previewTimestamps.map((ts) => {
      const key = quarterHourKey(ts);
      const val = key != null ? map.get(key) : null;
      const rounded = toTwoDecimals(val);
      return rounded == null ? "" : String(rounded);
    });
    setAutoOverrideValues(next);
    setAutoOverrideSeries(next.join(" "));
  }, [
    previewOpen,
    previewMode,
    scheduleInputForecast,
    previewMtuCount,
    previewTimestamps,
    setAutoOverrideValues,
    setAutoOverrideSeries,
  ]);

  useEffect(() => {
    if (!previewOpen || previewMode !== "automatic") return;
    if (!currentProject?.id) return;
    let active = true;
    const fetchScheduleForecast = async () => {
      try {
        const data = await getScheduleSettings(currentProject.id, accessToken);
        if (!active) return;
        const parsedForecast =
          Array.isArray(data?.input_forecast)
            ? data.input_forecast
            : tryParseJson(data?.input_forecast);
        setScheduleInputForecast(Array.isArray(parsedForecast) ? parsedForecast : []);
      } catch {
        if (active) setScheduleInputForecast([]);
      }
    };
    fetchScheduleForecast();
    return () => {
      active = false;
    };
  }, [previewOpen, previewMode, currentProject?.id, accessToken]);

  useEffect(() => {
    if (!previewOpen || previewMode !== "manual") return;
    if (forecastLoading || forecastSeriesDay !== deliveryDay) return;
    const series = forecastSeries.p50 || [];
    if (series.length !== previewMtuCount || previewMtuCount === 0) return;
    setManualOverrideValues((prev) => {
      const hasValues = prev.some((v) => String(v || "").trim() !== "");
      if (hasValues) return prev;
      const next = series.map((v) => (v == null ? "" : String(v)));
      setManualOverrideSeries(next.join(" "));
      return next;
    });
  }, [
    previewOpen,
    previewMode,
    forecastSeries.p50,
    forecastLoading,
    forecastSeriesDay,
    deliveryDay,
    previewMtuCount,
    setManualOverrideValues,
    setManualOverrideSeries,
  ]);

  const overrideFilledCount = useMemo(
    () => activeOverrideValues.filter((v) => String(v || "").trim() !== "").length,
    [activeOverrideValues]
  );
  const selectedMtuCount = useMemo(
    () => activeSelectedMtus.filter(Boolean).length,
    [activeSelectedMtus]
  );
  const allMtusSelected = previewMtuCount > 0 && selectedMtuCount === previewMtuCount;
  const partiallySelected = selectedMtuCount > 0 && !allMtusSelected;
  const hasOverride = overrideFilledCount > 0;
  const forecastValueCount = useMemo(
    () => (forecastSeries.p50 || []).filter((v) => v != null && v !== "").length,
    [forecastSeries.p50]
  );
  const forecastReadyForPreview =
    !forecastLoading &&
    !forecastError &&
    previewMtuCount > 0 &&
    forecastValueCount === previewMtuCount;


  const applySeriesToSelectedMtus = useCallback(
    (seriesKey) => {
      const sourceSeries = forecastSeries?.[seriesKey] || [];
      const next = previewTimestamps.map((_, idx) =>
        activeSelectedMtus[idx]
          ? getSeriesValueAt(sourceSeries, idx)
          : activeOverrideValues[idx] ?? ""
      );
      setActiveOverrideValues(next);
      setActiveOverrideSeries(next.join(" "));
    },
    [
      activeOverrideValues,
      activeSelectedMtus,
      forecastSeries,
      previewTimestamps,
      setActiveOverrideSeries,
      setActiveOverrideValues,
    ]
  );


// ------------------ FILTERED / SORTED HISTORY ------------------
  const filteredHistory = useMemo(() => {
    const normalizeYMD = (value) => {
      if (!value) return "";
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;
      }
      if (typeof value === "string") {
        const trimmed = value.trim();
        const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
          return `${match[1]}-${match[2]}-${match[3]}`;
        }
        const parsed = new Date(trimmed);
        if (!Number.isNaN(parsed.getTime())) {
          return `${parsed.getFullYear()}-${pad2(parsed.getMonth() + 1)}-${pad2(parsed.getDate())}`;
        }
        if (trimmed.length >= 10) return trimmed.slice(0, 10);
      }
      return "";
    };

    const items = history.filter((h) => {
      if (historyDate && normalizeYMD(h.start) !== historyDate) return false;
      if (historyDeliveryDay && !runIncludesDeliveryDay(h, historyDeliveryDay, projectTimeZone)) return false;
      return true;
    });

    // Sort by start time DESC (latest at top)
    items.sort((a, b) => new Date(b.start || 0) - new Date(a.start || 0));

    return items;
  }, [history, historyDate, historyDeliveryDay, projectTimeZone]);

  const historyTotalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredHistory.length / 20)),
    [filteredHistory.length]
  );

  useEffect(() => {
    setHistoryPage(0);
  }, [historyDate, historyDeliveryDay, history.length]);

  useEffect(() => {
    if (historyPage > historyTotalPages - 1) {
      setHistoryPage(historyTotalPages - 1);
    }
  }, [historyPage, historyTotalPages]);

  const handleDownloadLatestPvDetails = useCallback(async () => {
    if (!currentProject?.id || pvDetailsDownloading) return;

    try {
      setPvDetailsDownloading(true);
      const { blob, filename } = await downloadLatestPvDetails(
        String(currentProject.id),
        accessToken
      );
      triggerFileDownload(
        blob,
        filename || `${currentProject?.name || currentProject.id}_pv_details.xlsx`
      );
      setStatus({ sev: "success", msg: "Latest PV details report downloaded." });
    } catch (err) {
      if (err?.status === 404) {
        setStatus({ sev: "warning", msg: "PV details report is not available yet." });
      } else {
        setStatus({
          sev: "error",
          msg: err?.message || "Failed to download PV details report.",
        });
      }
    } finally {
      setPvDetailsDownloading(false);
    }
  }, [accessToken, currentProject?.id, currentProject?.name, pvDetailsDownloading]);

  // ------------------ UI ------------------
  return (
    <>
      <Box sx={{ position: "relative" }}>
        <Box
          sx={
            marketStrategyDisabled
              ? {
                  filter: "blur(6px)",
                  pointerEvents: "none",
                  userSelect: "none",
                }
              : undefined
          }
        >
          <Paper
            elevation={4}
            sx={{
              p: { xs: 1.5, md: 2 },
              borderRadius: 3,
              backgroundColor: (t) => t.palette.background.paper,
            }}
          >
            <Stack spacing={1.5}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              alignItems: { xs: "flex-start", lg: "flex-start" },
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Market Revenue Optimization
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
                Optimize battery dispatch and bidding strategies to maximize revenues based on Day-Ahead market price forecasts.
              </Typography>
            </Box>
            {demoProfile?.demoModeEnabled ? (
              <Alert
                severity={marketDemoRestrictionMessage ? "warning" : "info"}
                sx={{
                  minWidth: { xs: "100%", lg: 340 },
                  mb: 0,
                  "& .MuiAlert-message": { width: "100%" },
                }}
              >
                {marketDemoRestrictionMessage ? (
                  marketDemoRestrictionMessage
                ) : (
                  <>
                    <Box sx={{ fontWeight: 700 }}>Remaining market optimizations today</Box>
                    <Box>
                      {demoProfile.marketOptimizationRemainingToday} /{" "}
                      {demoProfile.demoMarketOptimizationLimitPerDay}
                    </Box>
                  </>
                )}
              </Alert>
            ) : null}
          </Box>

          <Divider />

          <Box
            sx={{
              p: 1.25,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: (t) =>
                t.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#f5faff",
              width: "100%",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1.25}
              flexWrap="wrap"
            >
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontSize: "0.85rem",
                    color: "text.primary",
                  }}
                >
                  Market Participation
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Select which markets this project intends to bid into.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                {MARKET_PARTICIPATION_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.key}
                    sx={{ mr: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={Boolean(marketParticipation[option.key])}
                        disabled={option.locked}
                        onChange={handleMarketParticipationChange(option.key)}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.label}
                      </Typography>
                    }
                  />
                ))}
              </Stack>
            </Stack>
            <Alert severity="warning" variant="outlined" sx={{ mt: 1.25 }}>
              Pending backend support: only DAM currently drives the dispatch run. Selecting IDA1/IDA2/IDA3
              here saves your intended market participation but does not yet change the optimization result.
            </Alert>
          </Box>

          <Box
            sx={{
              p: 1.25,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: (t) =>
                t.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#f5faff",
              width: "100%",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1.25}
            >
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontSize: "0.85rem",
                    color: "text.primary",
                  }}
                >
                  Day-Ahead Market(DAM) Price forecast input
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Select which percentile will drive the optimization. Each run covers one delivery day: normally 96 values, or 92 / 100 when daylight saving time changes.
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    py: 0.375,
                    borderRadius: 2,
                    backgroundColor: (t) =>
                      forecastStatusLoading
                        ? (t.palette.mode === "dark" ? "rgba(144,202,249,0.16)" : "rgba(33,150,243,0.12)")
                        : forecastReady
                          ? (t.palette.mode === "dark" ? "rgba(165,214,167,0.16)" : "rgba(76,175,80,0.12)")
                          : (t.palette.mode === "dark" ? "rgba(255,224,178,0.18)" : "rgba(255,152,0,0.16)"),
                    color: forecastStatusLoading
                      ? "info.main"
                      : forecastReady
                        ? "success.main"
                        : "warning.main",
                    fontWeight: 700,
                  }}
                >
                  {forecastStatusLoading ? (
                    <CircularProgress size={16} sx={{ color: "info.main" }} />
                  ) : forecastReady ? (
                    <CheckCircleOutlineIcon fontSize="small" />
                  ) : (
                    <WarningAmberOutlinedIcon fontSize="small" />
                  )}
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                  >
                    {forecastStatusLoading
                      ? "Loading forecast (D+1)"
                      : forecastReady
                        ? "Forecast available (D+1)"
                        : "Forecast incomplete or missing (D+1)"}
                  </Typography>
                </Box>
                {/* "Available" and "how it was built" are two different
                    statements. A forecast can pass every gate and still have
                    been built from a smaller feature set or an estimated
                    input; that is worth one chip rather than a silent green. */}
                {forecastReady && forecastMode?.degraded && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="warning"
                    label={
                      forecastMode?.res_total_reduced?.reduced
                        ? "built without the wind/solar split"
                        : forecastMode?.res_proxy?.proxy
                          ? "built with proxy wind/solar"
                          : forecastMode?.load_proxy?.proxy
                            ? "built with a proxy load forecast"
                            : "built with cached weather"
                    }
                    title={
                      "This forecast passed every publish gate but was not built from a full "
                      + "set of TSO inputs. "
                      + (forecastMode?.res_total_reduced?.reduced
                        ? "The TSO published no wind/solar split for this day, so net load came "
                          + "from a published RES total and the two split features were dropped. "
                          + "Measured for DAM at 97.9% of a full-input day."
                        : forecastMode?.res_proxy?.proxy
                          ? "Wind/solar were estimated from a weather model because the TSO's "
                            + "day-ahead RES had not reached ENTSO-E at run time."
                          : forecastMode?.load_proxy?.proxy
                            ? "The load forecast was estimated because the TSO's had not reached "
                              + "ENTSO-E at run time."
                            : "Live weather was incomplete, so the last good weather was used.")
                    }
                  />
                )}
                <Tooltip title="Forecast percentiles are provided per MTU. D+1 forecast arrives around 08:10 local time of the project's set bidding zone.">
                  <InfoOutlinedIcon fontSize="small" color="action" />
                </Tooltip>
              </Stack>
            </Stack>

            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Choose a percentile in the table below and apply it to the Used value column.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "info.main",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mt: 0.35,
                  fontWeight: 800,
                }}
              >
                <InfoOutlinedIcon fontSize="small" />
                P50 is the default used value for optimization unless you override it in the table before the run is triggered.
              </Typography>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} mt={1.25} alignItems="center">
              <Box
                sx={{
                  flex: 1,
                  minWidth: 260,
                  p: 1,
                  borderRadius: 2,
                  border: "1px dashed",
                  borderColor: "divider",
                  backgroundColor: (t) =>
                    t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Automatic run
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  1 delivery day {"->"} {autoMtuCount} values (15-min MTU)
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>
                  Market delivery timezone: {projectTimeZone}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 260,
                  p: 1,
                  borderRadius: 2,
                  border: "1px dashed",
                  borderColor: "divider",
                  backgroundColor: (t) =>
                    t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Manual Run
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  1 delivery day {"->"} {manualMtuCount || 0} values (15-min MTU)
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>
                  Market delivery timezone: {projectTimeZone}
                </Typography>
              </Box>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} mt={1.25} alignItems="center">
              <Button
                variant="contained"
                onClick={() => {
                  setPreviewMode(auto ? "automatic" : "manual");
                  setPreviewOpen(true);
                }}
              >
                View price series
              </Button>
              {!forecastReady && (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Forecast data is not available yet.
                </Typography>
              )}
              {forecastStatusError && !forecastReady && (
                <Typography variant="caption" sx={{ color: "error.main" }}>
                  {forecastStatusError}
                </Typography>
              )}
            </Stack>
          </Box>

          <Divider />

          <Box
            data-dirty-ignore="true"
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", xl: "minmax(0, 1fr) minmax(0, 1fr)" },
              gap: 1.25,
              alignItems: "start",
            }}
          >
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: (t) =>
                t.palette.mode === "dark" ? t.palette.background.paper : "#f5faff",
              width: "100%",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              spacing={1}
            >
              <Box>
                 <Typography
                   variant="subtitle2"
                   sx={{
                     fontWeight: 600,
                     mb: 0.5,
                     textTransform: "uppercase",
                     letterSpacing: 0.5,
                     fontSize: "0.85rem",
                     color: "text.primary",
                   }}
                 >
                   Automatic Run
                 </Typography>
                 <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.35 }}>
                   Schedule a daily run for the project currently loaded.
                 </Typography>
                 {loadingSchedule && (
                     <Stack direction="row" spacing={0.5} alignItems="center" mt={0.5}>
                     <CircularProgress size={16} />
                     <Typography variant="caption" sx={{ color: "text.secondary" }}>
                       Loading schedule...
                     </Typography>
                   </Stack>
                 )}
              </Box>
              <Stack direction="row" spacing={0.35} alignItems="center" flexWrap="wrap">
                <FormControlLabel
                  sx={{ mr: 0.5 }}
                  control={
                    <Switch
                      checked={auto}
                      disabled={marketDemoActionDisabled && !auto}
                      onChange={(e) => {
                        const nextAuto = e.target.checked;
                        if (nextAuto && marketDemoRestrictionMessage) {
                          setScheduleStatus({
                            sev: "warning",
                            msg: marketDemoRestrictionMessage,
                          });
                          return;
                        }
                        setAuto(nextAuto);
                      }}
                    />
                  }
                  label="Enable Automatic Run"
                />
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 0.875,
                    py: 0.25,
                    borderRadius: 2,
                    backgroundColor: loadingSchedule
                      ? "rgba(33, 150, 243, 0.12)"
                      : scheduleActive
                        ? "rgba(76, 175, 80, 0.12)"
                        : "rgba(255, 152, 0, 0.16)",
                    color: loadingSchedule
                      ? "info.main"
                      : scheduleActive
                        ? "success.main"
                        : "warning.main",
                    fontWeight: 700,
                  }}
                >
                  {loadingSchedule ? (
                    <CircularProgress size={16} sx={{ color: "info.main" }} />
                  ) : scheduleActive ? (
                    <CheckCircleOutlineIcon fontSize="small" />
                  ) : (
                    <WarningAmberOutlinedIcon fontSize="small" />
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    {loadingSchedule ? "Loading" : scheduleActive ? "Active" : "Inactive"}
                  </Typography>
                </Box>
              </Stack>
            </Stack>

            <Box
              sx={{
                mt: 1,
                p: 1,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: (t) => t.palette.background.paper,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Schedule Settings
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 0.75, lineHeight: 1.35 }}
              >
                Set when the automatic run starts and review the timezone used for scheduling.
              </Typography>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Box sx={{ minWidth: 200 }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <TimePicker
                      label="Run time (HH:MM)"
                      value={time ? timeStringToDate(time) : null}
                      onChange={(val) => {
                        if (!val) {
                          setTime("");
                          return;
                        }
                        setTime(dateToTimeString(val));
                      }}
                      ampm={false}
                      views={["hours", "minutes"]}
                      disabled={!auto || loadingSchedule}
                      minTime={timeStringToDate(MIN_RUN_TIME)}
                      maxTime={timeStringToDate(MAX_RUN_TIME)}
                      timeSteps={{ hours: 1, minutes: 1 }}
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: { maxWidth: 190 },
                          disabled: !auto || loadingSchedule,
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    minWidth: 240,
                    maxWidth: 520,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
                      Run time timezone: {assetTimeZone}
                    </Typography>
                    <Tooltip title="Run time is interpreted in the local time of the project's set bidding zone, which matches the Project Settings timezone.">
                      <InfoOutlinedIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Stack>
                  {/* Project automatically inferred from loaded project */}

{scheduleNotice && (
                  <Alert
                    severity="error"
                    sx={{ px: 1.25, py: 0.25, alignItems: "center" }}
                  >
                    {scheduleNotice}
                  </Alert>
                )}


{auto && !currentProject?.id && (
                    <Alert
                      severity="warning"
                      sx={{ px: 1.25, py: 0.25, alignItems: "center" }}
                    >
                    Load a project before saving.
                    </Alert>
                )}
              </Box>
            </Stack>
          </Box>

          <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-start" }}>
            <Button
              variant="contained"
              onClick={handleSaveSchedule}
              disabled={
                savingSchedule ||
                loadingSchedule ||
                !currentProject?.id ||
                (!auto && !!scheduleNotice) ||
                (auto && marketDemoActionDisabled)
              }
            >
              {savingSchedule ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} /> Saving...
                </>
                ) : (
                  "Save"
                )}
              </Button>
            </Box>

          </Box>

          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: (t) =>
                t.palette.mode === "dark" ? t.palette.background.paper : "#f5faff",
              width: "100%",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 0.5,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                fontSize: "0.85rem",
                color: "text.primary",
              }}
            >
              Manual Trigger
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.75, lineHeight: 1.35 }}>
              Choose the delivery day and launch a one-time optimisation using the currently loaded project.
            </Typography>
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: (t) =>
                  t.palette.mode === "dark"
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(255,255,255,0.6)",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Delivery day
              </Typography>

              <Box>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                >
                  <AppDatePicker
                    label="Delivery day"
                    value={deliveryDay}
                    onChange={setDeliveryDay}
                    error={Boolean(deliveryDayError)}
                    helperText={deliveryDayError}
                    maxDate={tomorrowInProjectTimeZone}
                    sx={{ minWidth: 210, width: 230, flexShrink: 0 }}
                  />
                </Stack>
                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
                >
                  Market delivery timezone: {projectTimeZone}; {manualMtuCount || 0} settlement periods.
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "text.secondary" }}
                >
                  Delivery days up to D+1 are available, subject to complete price inputs.
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={1} mt={1}>
              <Button
                variant="contained"
                onClick={() => runNow({ isAuto: false })}
                disabled={
                  loading ||
                  marketDemoActionDisabled ||
                  Boolean(deliveryDayError) ||
                  !deliveryDay
                }
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} /> Running...
                  </>
                ) : (
                  "Run optimisation"
                )}
              </Button>
            </Stack>
          </Box>
          </Box>
            </Stack>
          </Paper>
        </Box>
        {marketStrategyDisabled && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 3,
              zIndex: 2,
            }}
          >
            <Alert
              severity="warning"
              sx={{
                maxWidth: 720,
                width: "100%",
                boxShadow: 6,
                alignItems: "flex-start",
                backdropFilter: "blur(10px)",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Market Strategy is unavailable for bidding zone {projectBiddingZone}.
              </Typography>
              <Typography variant="body2">
                The selected bidding zone is not supported for the Market Strategy features.
                Supported bidding zones: {supportedMarketsLabel}.
              </Typography>
            </Alert>
          </Box>
        )}
      </Box>

      {/* ------------- STATUS PANEL --------------- */}
      {status && (
        <Box mt={2}>
          <Alert
            severity={status.sev}
            variant="filled"
            action={
              status.sev === "success" || status.sev === "error" ? (
                <IconButton
                  aria-label="Dismiss status"
                  color="inherit"
                  size="small"
                  onClick={() => setStatus(null)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : undefined
            }
          >
            {status.sev === "error" ? mapSimulationError(status.msg, isGreek) : mapProgressMessage(status.msg, isGreek)}
          </Alert>

        </Box>
      )}

      {/* ------------- HISTORIC RUNS -------------- */}
      <Box mt={4}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Runs Overview
          </Typography>

        {/* Filter controls */}
        <Box mt={2} mb={2}>
          <Stack
            direction="row"
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            flexWrap="wrap"
            rowGap={1.5}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <AppDatePicker
                label="Filter by Execution Start"
                value={historyDate}
                onChange={setHistoryDate}
                slotProps={{ textField: { InputLabelProps: { shrink: true, sx: { whiteSpace: "nowrap" } } } }}
                sx={{
                  minWidth: 240,
                  maxWidth: 260,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "divider" },
                    "&:hover fieldset": { borderColor: "text.secondary" },
                    "&.Mui-focused fieldset": { borderColor: "primary.main" },
                  },
                }}
              />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <AppDatePicker
                label="Filter by Delivery Day"
                value={historyDeliveryDay}
                onChange={setHistoryDeliveryDay}
                slotProps={{ textField: { InputLabelProps: { shrink: true, sx: { whiteSpace: "nowrap" } } } }}
                sx={{
                  minWidth: 240,
                  maxWidth: 260,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "divider" },
                    "&:hover fieldset": { borderColor: "text.secondary" },
                    "&.Mui-focused fieldset": { borderColor: "primary.main" },
                  },
                }}
              />
            </Stack>

            {(historyDate || historyDeliveryDay) && (
              <Button
                variant="text"
                onClick={() => {
                  setHistoryDate("");
                  setHistoryDeliveryDay("");
                }}
                sx={{ textTransform: "none" }}
              >
                Clear filters
              </Button>
            )}

            {isHybridSystem && (
              <Tooltip title="Downloads the latest PV details Excel report from the most recent optimization run for this project.">
                <span>
                  <Button
                    variant="outlined"
                    onClick={handleDownloadLatestPvDetails}
                    disabled={!currentProject?.id || pvDetailsDownloading}
                    startIcon={
                      pvDetailsDownloading ? <CircularProgress size={16} thickness={5} /> : null
                    }
                    sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                  >
                    {pvDetailsDownloading
                      ? "Downloading..."
                      : "Latest Optimization PV Details"}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Box>

        {/* Table view */}
        {filteredHistory.length === 0 ? (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            No runs for the selected filters.
          </Typography>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ boxShadow: "none", border: "none" }}
          >
              <Table
                size="small"
                sx={{
                  "& th": {
                    fontWeight: 700,
                    fontSize: 14,
                  py: 0.35,
                  px: 0.6,
                  whiteSpace: "nowrap",
                },
                "& td": {
                  fontSize: 14,
                  py: 0.28,
                  px: 0.6,
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 48 }} />
                  <TableCell sx={{ width: 110 }}>Market Type</TableCell>
                  <TableCell sx={{ width: 180 }}>Execution Start</TableCell>
                  <TableCell sx={{ width: 180 }}>Execution Finish</TableCell>
                  <TableCell sx={{ width: 110 }}>Status</TableCell>
                  <TableCell sx={{ width: 110 }}>Automatic</TableCell>
                  <TableCell sx={{ width: 210 }}>Delivery Day</TableCell>
                  <TableCell sx={{ width: 100 }}>Error</TableCell>
                  <TableCell align="right" sx={{ width: 110 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHistory
                  .slice(historyPage * 20, historyPage * 20 + 20)
                  .map((h, i) => {
                    const mapped = mapHistoryStatus(h.status, h.error);
                    const showError = mapped.severity === "error";
                    const errText = getUserFacingErrorMessage(h.error);
                    const fmtDate = (value) => {
                      if (!value) return "N/A";
                      const d = new Date(value);
                      return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString("en-GB");
                    };
                    const fmtAutomatic = (value) => {
                      if (typeof value === "string") return value;
                      return value ? "Yes" : "No";
                    };
                    const isRunning =
                      h.pending ||
                      String(h.status || "").toLowerCase().includes("running") ||
                      String(h.status || "").toLowerCase().includes("started");
                    const icon = isRunning
                      ? <CircularProgress size={18} thickness={5} />
                      : mapped.severity === "success"
                        ? <CheckCircleOutlineIcon color="success" fontSize="small" />
                        : mapped.severity === "error"
                          ? <ErrorOutlineOutlinedIcon color="error" fontSize="small" />
                          : mapped.severity === "warning"
                            ? <WarningAmberOutlinedIcon color="warning" fontSize="small" />
                            : <InfoOutlinedIcon color="info" fontSize="small" />;
                    const statusColor = isRunning
                      ? "primary.main"
                      : mapped.severity === "success"
                        ? "success.main"
                        : mapped.severity === "error"
                          ? "error.main"
                          : "text.primary";
                    const statusLabel = isRunning ? "Running" : mapped.label;
                    return (
                      <TableRow
                        key={`${h.id || i}-${historyPage}-${i}`}
                        sx={{
                          "&:hover": {
                            backgroundColor: (t) => t.palette.action.hover,
                            boxShadow: (t) => t.shadows[1],
                          },
                        }}
                      >
                        <TableCell sx={{ width: 48 }}>{icon}</TableCell>
                        <TableCell>DA</TableCell>
                        <TableCell>{fmtDate(h.start)}</TableCell>
                        <TableCell>{isRunning ? "-" : fmtDate(h.end)}</TableCell>
                        <TableCell sx={{ color: statusColor, fontWeight: 600 }}>
                          {mapProgressMessage(statusLabel, isGreek)}
                        </TableCell>
                        <TableCell>{fmtAutomatic(h.automatic)}</TableCell>
                        <TableCell>{formatRunDeliveryPeriod(h, projectTimeZone)}</TableCell>
                        <TableCell>
                          {showError ? (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                setErrorDialog({
                                  open: true,
                                  message: errText,
                                })
                              }
                              sx={{ textTransform: "none" }}
                            >
                              Show
                            </Button>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {!isRunning && h.id ? (
                            <Button
                              size="small"
                              variant="text"
                              color="primary"
                              endIcon={<ArrowForwardIosIcon fontSize="inherit" />}
                              onClick={() => goToRunResults(h.id)}
                              disableRipple
                              disableFocusRipple
                              disableElevation
                              sx={{
                                textTransform: "none",
                                fontWeight: 700,
                                fontSize: 12,
                                minWidth: "auto",
                                px: 0,
                                py: 0,
                                color: "primary.main",
                                backgroundColor: "transparent !important",
                                border: "none",
                                borderRadius: 0,
                                "&:hover": { backgroundColor: "transparent !important", border: "none" },
                                "&:focus": { outline: "none" },
                                "&:focus-visible": { outline: "none" },
                                "& .MuiButton-endIcon": { marginLeft: 0.3 },
                                "& .MuiTouchRipple-root": { display: "none" },
                              }}
                            >
                              View Results
                            </Button>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1, py: 1 }}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Page {historyPage + 1} of {historyTotalPages}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <IconButton
                  size="small"
                  onClick={() => setHistoryPage(0)}
                  disabled={historyPage === 0}
                  aria-label="First page"
                >
                  <FirstPageIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setHistoryPage((prev) => Math.max(0, prev - 1))}
                  disabled={historyPage === 0}
                  aria-label="Previous page"
                >
                  <KeyboardArrowLeftIcon fontSize="small" />
                </IconButton>
                <Pagination
                  count={historyTotalPages}
                  page={historyPage + 1}
                  onChange={(_, page) => setHistoryPage(page - 1)}
                  size="small"
                  siblingCount={1}
                  boundaryCount={1}
                  hidePrevButton
                  hideNextButton
                />
                <IconButton
                  size="small"
                  onClick={() => setHistoryPage((prev) => Math.min(historyTotalPages - 1, prev + 1))}
                  disabled={historyPage >= historyTotalPages - 1}
                  aria-label="Next page"
                >
                  <KeyboardArrowRightIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setHistoryPage(historyTotalPages - 1)}
                  disabled={historyPage >= historyTotalPages - 1}
                  aria-label="Last page"
                >
                  <LastPageIcon fontSize="small" />
                </IconButton>
                <TextField
                  size="small"
                  type="number"
                  value={historyGoTo}
                  onChange={(e) => setHistoryGoTo(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    const next = Number.parseInt(historyGoTo, 10);
                    if (!Number.isFinite(next)) return;
                    const clamped = Math.min(Math.max(next, 1), historyTotalPages);
                    setHistoryPage(clamped - 1);
                  }}
                  sx={{
                    "& .MuiInputBase-root": { height: 28 },
                    "& .MuiInputBase-input": { py: 0, fontSize: 12 },
                  }}
                  inputProps={{ min: 1, max: historyTotalPages, style: { textAlign: "center", width: 32 } }}
                />
              </Stack>
            </Box>
          </TableContainer>
        )}
      </Box>

      {scheduleStatus && (
        <Snackbar
          open
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={4000}
          onClose={() => setScheduleStatus(null)}
        >
          <Alert
            severity={scheduleStatus.sev || "success"}
            variant="filled"
            sx={{ borderRadius: 2, fontWeight: 700 }}
            icon={<CheckCircleOutlineIcon fontSize="small" />}
          >
            {scheduleStatus.msg}
          </Alert>
        </Snackbar>
      )}

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Forecast Series</DialogTitle>
        <DialogContent dividers>
          <Box
            component="form"
            id="forecast-series-form"
            onSubmit={async (e) => {
              e.preventDefault();
              if (previewMode === "automatic" && currentProject?.id) {
                setPreviewSaving(true);
                try {
                  const payload = previewTimestamps.map((ts, idx) => {
                    const value = toTwoDecimals(autoOverrideValues[idx]);
                    const localTs =
                      ts instanceof Date ? formatDateTimeWithOffset(ts, projectTimeZone) : String(ts || "");
                    return { ts: localTs, price_eur_mwh: value };
                  });
                  const hasMissing = payload.some((row) => row.price_eur_mwh == null);
                  if (hasMissing) {
                    setScheduleStatus({
                      sev: "warning",
                      msg: "All Used values are required before saving to the schedule.",
                    });
                    setPreviewSaving(false);
                    return;
                  }
                  const updated = await updateScheduleInputForecast(
                    currentProject.id,
                    payload,
                    accessToken
                  );
                  if (updated?.input_forecast) {
                    setScheduleInputForecast(updated.input_forecast);
                  }
                  setScheduleStatus({
                    sev: "success",
                    msg: "Forecast overrides saved to schedule.",
                  });
                  setPreviewOpen(false);
                } catch (err) {
                  setScheduleStatus({
                    sev: "warning",
                    msg: `Failed to update schedule forecast (${err?.message || err})`,
                  });
                } finally {
                  setPreviewSaving(false);
                }
              } else if (previewMode === "manual") {
                setScheduleStatus({
                  sev: "success",
                  msg: "Values saved. Proceed to manual trigger.",
                });
                setPreviewOpen(false);
              }
              if (previewMode !== "automatic") setPreviewOpen(false);
            }}
          >
            <Stack spacing={1.25}>
            <FormControl component="fieldset" sx={{ mb: 0 }}>
              <FormLabel component="legend">Timetable mode</FormLabel>
              <RadioGroup
                row
                value={previewMode}
                onChange={(e) => setPreviewMode(e.target.value)}
              >
                <FormControlLabel value="automatic" control={<Radio />} label="Automatic (D+1)" />
                <FormControlLabel value="manual" control={<Radio />} label="Manual (delivery day)" />
              </RadioGroup>
            </FormControl>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: -0.25 }}>
              {previewRangeLabel}
            </Typography>

            <Box sx={{ mt: 0.25 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
                Final series used for the run
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.35 }}>
                Confirm which percentile will drive the optimization and adjust series if needed. Automatic run expects {autoMtuCount} values. Manual run expects {manualMtuCount || 0} values. MTUs are generated in {projectTimeZone}.
              </Typography>
              <Typography variant="h6" sx={{ mt: 0.25 }}>
                {overrideFilledCount || 0} override values provided
              </Typography>
            </Box>

            <Divider />

            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                maxHeight: 460,
                overflow: "hidden",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, px: 2, pt: 2, pb: 1 }}>
                Timetable (percentiles + used value)
              </Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding="none"
                      align="center"
                      sx={{
                        backgroundColor: "background.paper",
                        width: 84,
                        minWidth: 84,
                        px: 0.25,
                        pt: 0.25,
                        pb: 0.1,
                        verticalAlign: "bottom",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "center", minHeight: 54 }}>
                        <Stack spacing={0} alignItems="center" justifyContent="center" sx={{ lineHeight: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              whiteSpace: "nowrap",
                              mb: -0.25,
                              fontSize: 10.5,
                              width: "100%",
                              textAlign: "center",
                            }}
                          >
                            {selectedMtuCount}/{previewMtuCount} selected
                          </Typography>
                          <Checkbox
                            size="small"
                            checked={allMtusSelected}
                            indeterminate={partiallySelected}
                            sx={{ p: 0.25, alignSelf: "center" }}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setActiveSelectedMtus(() =>
                                Array.from({ length: previewMtuCount }, () => checked)
                              );
                            }}
                          />
                        </Stack>
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        backgroundColor: "background.paper",
                        pt: 0.25,
                        pb: 0.1,
                        px: 0.5,
                        verticalAlign: "bottom",
                        minWidth: 220,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "center", minHeight: 54 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.1, mb: 0 }}>
                          MTU
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 700, backgroundColor: "background.paper", pt: 0.25, pb: 0.1, verticalAlign: "bottom" }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "center", minHeight: 54 }}>
                        <Stack spacing={0.5} alignItems="center" justifyContent="center">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => applySeriesToSelectedMtus("p10")}
                            disabled={forecastLoading || previewTimestamps.length === 0 || selectedMtuCount === 0}
                          >
                            Apply
                          </Button>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            P10
                          </Typography>
                        </Stack>
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 700, backgroundColor: "background.paper", pt: 0.25, pb: 0.1, verticalAlign: "bottom" }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "center", minHeight: 54 }}>
                        <Stack spacing={0.5} alignItems="center" justifyContent="center">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => applySeriesToSelectedMtus("p50")}
                            disabled={forecastLoading || previewTimestamps.length === 0 || selectedMtuCount === 0}
                          >
                            Apply
                          </Button>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            P50
                          </Typography>
                        </Stack>
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 700, backgroundColor: "background.paper", pt: 0.25, pb: 0.1, verticalAlign: "bottom" }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "center", minHeight: 54 }}>
                        <Stack spacing={0.5} alignItems="center" justifyContent="center">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => applySeriesToSelectedMtus("p90")}
                            disabled={forecastLoading || previewTimestamps.length === 0 || selectedMtuCount === 0}
                          >
                            Apply
                          </Button>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            P90
                          </Typography>
                        </Stack>
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: 700, backgroundColor: "background.paper", pt: 0.25, pb: 0.1, verticalAlign: "bottom" }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "center", minHeight: 54 }}>
                        <Stack spacing={0.5} alignItems="center" justifyContent="center">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => applySeriesToSelectedMtus("actual")}
                            disabled={forecastLoading || previewTimestamps.length === 0 || selectedMtuCount === 0}
                          >
                            Apply
                          </Button>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Actual
                          </Typography>
                        </Stack>
                      </Box>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        backgroundColor: "background.paper",
                        pt: 0.25,
                        pb: 0.1,
                        verticalAlign: "bottom",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "center", minHeight: 54 }}>
                        Used value ({forecastUnit})
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewTimestamps.map((_, idx) => {
                    const p10Val = forecastSeries.p10?.[idx];
                    const p50Val = forecastSeries.p50?.[idx];
                    const p90Val = forecastSeries.p90?.[idx];
                    const actualVal = forecastSeries.actual?.[idx];
                    return (
                      <TableRow key={`override-${idx}`}>
                        <TableCell
                          padding="none"
                          align="center"
                          sx={{ width: 84, minWidth: 84, px: 0.25, py: 0.25 }}
                        >
                          <Checkbox
                            size="small"
                            checked={Boolean(activeSelectedMtus[idx])}
                            sx={{ p: 0.25 }}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setActiveSelectedMtus((prev) => {
                                const next = [...prev];
                                next[idx] = checked;
                                return next;
                              });
                            }}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ color: "text.secondary", whiteSpace: "nowrap", py: 0.75, px: 0.5 }}
                        >
                          {formatMtuRange(previewTimestamps, idx, projectTimeZone)}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.75 }}>
                          {p10Val === "" || p10Val == null ? "-" : p10Val}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.75 }}>
                          {p50Val === "" || p50Val == null ? "-" : p50Val}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.75 }}>
                          {p90Val === "" || p90Val == null ? "-" : p90Val}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.75 }}>
                          {actualVal === "" || actualVal == null ? "-" : actualVal}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <TextField
                            size="small"
                            sx={{
                              width: 170,
                              "& .MuiInputBase-root": {
                                minHeight: 34,
                              },
                              "& .MuiInputBase-input": {
                                py: 0.75,
                              },
                            }}
                            value={activeOverrideValues[idx] ?? ""}
                            onChange={(e) => {
                              const next = [...activeOverrideValues];
                              const raw = e.target.value;
                              if (!/^-?\d*(\.\d{0,2})?$/.test(raw)) return;
                              next[idx] = raw;
                              setActiveOverrideValues(next);
                              setActiveOverrideSeries(next.join(" "));
                            }}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">{forecastUnit}</InputAdornment>,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </TableContainer>
            </Box>

            {previewMtuCount > 0 && overrideFilledCount > 0 && overrideFilledCount !== previewMtuCount && (
              <Alert severity="warning">
                Override series has {overrideFilledCount} values, but the run expects {previewMtuCount}.
              </Alert>
            )}

            <Alert
              severity={forecastLoading ? "info" : forecastError ? "warning" : forecastReadyForPreview ? "info" : "warning"}
            >
              {forecastLoading
                ? "Loading forecast values..."
                : forecastError
                  ? "Forecast values are not available for the selected window."
                  : forecastReadyForPreview
                    ? "Forecast values are ready to use."
                    : "Forecast values are not ready yet for Day Ahead (D+1)."}
            </Alert>

          </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            type="submit"
            form="forecast-series-form"
            disabled={
              previewSaving ||
              !hasOverride ||
              loading ||
              (previewMode === "manual" &&
                (Boolean(deliveryDayError) || !deliveryDay))
            }
          >
            {previewSaving ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1 }} /> Saving...
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={errorDialog.open}
        onClose={() => setErrorDialog({ open: false, message: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Error Details</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {errorDialog.message ? mapSimulationError(errorDialog.message, isGreek) : (isGreek ? "Δεν δόθηκαν λεπτομέρειες σφάλματος." : "Error details not provided")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialog({ open: false, message: "" })}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
